import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseurlService } from '../../services/baseurl.service';

export type MailFilterOperator =
  | 'EQUALS'
  | 'NOT_EQUALS'
  | 'CONTAINS'
  | 'IN'
  | 'EXISTS'
  | 'NOT_EXISTS'
  | 'GT'
  | 'GTE'
  | 'LT'
  | 'LTE';

export const MAIL_FILTER_OPERATORS: MailFilterOperator[] = [
  'EQUALS',
  'NOT_EQUALS',
  'CONTAINS',
  'IN',
  'EXISTS',
  'NOT_EXISTS',
  'GT',
  'GTE',
  'LT',
  'LTE',
];

export const MAIL_FILTER_FIELDS: string[] = [
  'folder',
  'fromEmail',
  'fromName',
  'from',
  'subject',
  'flags',
  'receivedDate',
  '@timestamp',
];

export interface MailFilter {
  field: string;
  operator: MailFilterOperator;
  value?: string | null;
  values?: string[] | null;
}

export interface MailSearchRequest {
  filters: MailFilter[];
  freeText?: string | null;
  page: number;
  size: number;
  sortField: string;
  sortOrder: 'asc' | 'desc';
}

export interface MailSearchHitDto {
  elasticDocumentId: string;
  index: string;
  messageId: string;
  subject: string | null;
  from: string | null;
  fromEmail: string | null;
  fromName: string | null;
  folder: string | null;
  receivedDate: string | null;
  flags: string[];
  snippet: string | null;
}

export interface MailSearchResponse {
  hits: MailSearchHitDto[];
  total: number;
  page: number;
  size: number;
}

export interface MailFolderDto {
  folder: string;
  count: number;
}

export interface MailAttachmentDto {
  index: number;
  fileName: string | null;
  contentType: string;
  sizeBytes: number;
}

export interface MailDetailDto {
  messageId: string;
  folder: string;
  subject: string | null;
  from: string | null;
  to: string[];
  cc: string[];
  sentDate: string | null;
  receivedDate: string | null;
  flags: string[];
  headers: { [key: string]: string[] };
  textBody: string | null;
  htmlBody: string | null;
  attachments: MailAttachmentDto[];
}

@Injectable({
  providedIn: 'root'
})
export class MailService {
  constructor(
    private http: HttpClient,
    private baseurlService: BaseurlService,
  ) {}

  search(request: MailSearchRequest): Observable<MailSearchResponse> {
    return this.http.post<MailSearchResponse>(`${this.baseurlService.getBaseUrl()}/mail/search`, request);
  }

  getFolders(): Observable<MailFolderDto[]> {
    return this.http.get<MailFolderDto[]>(`${this.baseurlService.getBaseUrl()}/mail/folders`);
  }

  getDetail(messageId: string, folder?: string | null): Observable<MailDetailDto> {
    let params = new HttpParams().set('messageId', messageId);
    if (folder) {
      params = params.set('folder', folder);
    }
    return this.http.get<MailDetailDto>(`${this.baseurlService.getBaseUrl()}/mail/detail`, { params });
  }

  getRaw(messageId: string, folder?: string | null): Observable<Blob> {
    let params = new HttpParams().set('messageId', messageId);
    if (folder) {
      params = params.set('folder', folder);
    }
    return this.http.get(`${this.baseurlService.getBaseUrl()}/mail/raw`, { params, responseType: 'blob' });
  }

  getAttachment(messageId: string, index: number, folder?: string | null): Observable<Blob> {
    let params = new HttpParams().set('messageId', messageId).set('index', index.toString());
    if (folder) {
      params = params.set('folder', folder);
    }
    return this.http.get(`${this.baseurlService.getBaseUrl()}/mail/attachment`, { params, responseType: 'blob' });
  }
}
